import clsx from 'clsx';
import { ForwardedRef, forwardRef, MouseEvent, ReactNode, useCallback, useMemo } from 'react';

import { Spinner } from '~components/generic/spinner/Spinner';
import styles from '../Editor.module.css';

export type ChipProps = {
  onClick?: (event?: MouseEvent<HTMLElement>) => void;
  label?: string;
  title?: string;
  preIcon?: string | ReactNode;
  icon?: string | ReactNode;
  disabled?: boolean;
  processing?: boolean;
  size?: 'small' | 'medium' | 'large';
  splitIcon?: boolean;
  status?: 'default' | 'success' | 'error';

  _forceIdleState?: boolean;
  _forceActiveState?: boolean;
  _forceDisabledState?: boolean;
  _forceHoverState?: boolean;
  _forceProcessingState?: boolean;
};

export const Chip = forwardRef(
  (
    {
      onClick,
      title,
      icon,
      disabled,
      processing,
      preIcon,
      label,
      size = 'medium',
      splitIcon = false,
      status = 'default',
      _forceIdleState,
      _forceActiveState,
      _forceDisabledState,
      _forceHoverState,
      _forceProcessingState,
      ...passThroughProps
    }: ChipProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const handleOnClick = useCallback(
      (e: MouseEvent<HTMLElement>) => {
        if (!disabled && !processing && onClick) {
          onClick(e);
        }
      },
      [disabled, onClick, processing],
    );

    const isClickable = useMemo(() => onClick !== undefined, [onClick]);

    const clsChip = clsx(
      _forceIdleState && styles.chipIdle,
      _forceActiveState && styles.chipActive,
      _forceDisabledState && styles.chipDisabled,
      _forceHoverState && styles.chipHover,
      _forceProcessingState && styles.chipProcessing,
      !(_forceIdleState || _forceActiveState || _forceDisabledState || _forceHoverState || _forceProcessingState) && {
        [styles.chipDefault]: !disabled && !processing && !isClickable,
        [styles.chipClickable]: !disabled && !processing && isClickable,
        [styles.chipDisabled]: disabled,
        [styles.chipProcessing]: !disabled && processing,
      },
      size === 'small' && styles.chipSizeSmall,
      status === 'error' && styles.chipStatusError,
      status === 'success' && styles.chipStatusSuccess,
    );

    return (
      <div className={clsChip} title={title} onClick={handleOnClick} ref={ref} {...passThroughProps}>
        <div className={styles.chipInner}>
          {!!preIcon &&
            (typeof preIcon === 'string' ? (
              <div className={styles.chipPreIcon}>
                <i className={`c-icon-custom c-icon-custom--vertical-centered c-icon-custom--${preIcon}`} />
              </div>
            ) : (
              <div className={styles.chipPreIconComponent}>{preIcon}</div>
            ))}
          {!!label && <div className={styles.chipLabel}>{label}</div>}
          {!!splitIcon && !!icon && <div className={styles.chipIconSplitter} />}
          {!!icon &&
            (typeof icon === 'string' ? (
              <div className={styles.chipIcon}>
                <i className={`c-icon-custom c-icon-custom--vertical-centered c-icon-custom--${icon}`} />
              </div>
            ) : (
              <div className={styles.chipIconComponent}>{icon}</div>
            ))}
        </div>
        <div className={styles.chipProcessingIndicator}>
          <Spinner className={styles.spinner} />
        </div>
      </div>
    );
  },
);
Chip.displayName = 'Chip';
